import { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Collapse,
} from '@mui/material';
import { ExpandMore, Repeat, Handyman, Build } from '@mui/icons-material';
import { ThemePalette } from 'mui.theme';
import { PNG } from 'components/Asset/PNG';
import { getImageName } from 'utils/helpers/mapAssetImage';
import { TableView } from './TableView';
import { ChartView } from './ChartView';
import { PMsView } from './PMsView';
import { UpcomingPMsView } from './UpcomingPms';
import { CostView } from './CostView';
import { UnExpectedDowntimeView } from './UnExpectedDowntime';
import { PMsDueView } from './PmsDue';
import { Footer } from './Footer';
import { DATE_FORMAT } from 'utils/constants/dateTime';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import type { SquaresAlert, AlertFeedbackPayload } from 'types/squares.type';
import { DateView } from './DateView';
import type { Asset } from 'types/asset.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const AlertItem = ({
  enableDarkTheme,
  notification,
  acknowledgeNotification,
  assets,
  submitFeedback,
}: {
  enableDarkTheme: boolean;
  notification: SquaresAlert;
  acknowledgeNotification: (notificationId: string) => void;
  assets: Asset[];
  submitFeedback: (
    notificationId: string,
    userFeedback: AlertFeedbackPayload
  ) => void;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const lowercaseName = (notification.notificationName || '').toLowerCase();

  const displayType: string | null =
    lowercaseName === 'overdue-pms'
      ? 'pms'
      : lowercaseName.includes('repeat') || lowercaseName.includes('detect')
      ? 'repeat'
      : lowercaseName.includes('unexpected')
      ? 'unexpected'
      : lowercaseName.includes('cost')
      ? 'cost'
      : lowercaseName.includes('date')
      ? 'date'
      : lowercaseName === 'upcoming-pms'
      ? 'upcomingPms'
      : lowercaseName === 'pms-due'
      ? 'pmsDue'
      : null;

  const IconToDisplay =
    displayType === 'repeat'
      ? Repeat
      : displayType === 'pms'
      ? Handyman
      : Build;
  const ViewComponent =
    displayType === 'repeat'
      ? TableView
      : displayType === 'pms'
      ? PMsView
      : displayType === 'unexpected'
      ? UnExpectedDowntimeView
      : displayType === 'cost'
      ? CostView
      : displayType === 'date'
      ? DateView
      : displayType === 'upcomingPms'
      ? UpcomingPMsView
      : displayType === 'pmsDue'
      ? PMsDueView
      : ChartView;

  const severityColor =
    notification.severity === 'med'
      ? '#ff9800'
      : notification.severity === 'high'
      ? '#F44336'
      : '#039161';
  const [itemOpen, setItemOpen] = useState(false);
  const fontColor = palette.textPrimary;

  const handleClick = () => {
    if (displayType) {
      setItemOpen((prev) => !prev);
    }
  };

  const truncateText = (html: string, maxChars: number) => {
    const text = html.replace(/<[^>]*>/g, '');
    if (text.length <= maxChars) return html;

    return `${text.slice(0, maxChars)}...`;
  };

  const notificationAsset =
    assets.find((item) => item.bumperNumber === notification.assetName) ??
    assets[0];

  function acknowledgeAlert() {
    acknowledgeNotification(notification.notificationID);
  }

  const showPlusIcon =
    displayType === 'upcomingPms' && notification?.data?.length > 0;
  const plusIconCount =
    notification?.data?.reduce((set, item) => {
      set.add(item.asset);
      return set;
    }, new Set()).size || 0;

  return (
    <Box
      sx={{
        background: enableDarkTheme
          ? palette.modalBackground
          : ThemePalette.light.topBar,
        marginTop: '10px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: palette.textPrimary,
          height: '40px',
          border: '1px solid #1e3a5f',
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          borderBottomLeftRadius: itemOpen ? '0px' : '4px',
          borderBottomRightRadius: itemOpen ? '0px' : '4px',
          background: enableDarkTheme
            ? palette.modalBackground
            : palette.boxBackground,
          padding: '20px',
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: '#FF0000',
              width: 40,
              height: 40,
            }}
          >
            <IconToDisplay sx={{ fontSize: 22 }} />
          </Avatar>
          <Typography sx={{ color: fontColor, fontSize: 14, fontWeight: 600 }}>
            {notification.notificationName.toUpperCase()}
          </Typography>
        </Box>

        {!itemOpen && (
          <>
            <Box
              sx={{
                flex: 0.8,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <PNG
                name={getImageName(notificationAsset, 'Small')}
                alt={'ALT'}
                style={{
                  height: '60px',
                  width: '80px',
                  marginLeft: '10px',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
              <Box>
                <Typography
                  sx={{ color: fontColor, fontSize: 14, fontWeight: 500 }}
                >
                  {showPlusIcon
                    ? notification.data[0].asset
                    : notification.assetName}
                  {showPlusIcon && plusIconCount > 0 && (
                    <sup
                      style={{
                        marginLeft: '5px',
                        fontSize: '8px',
                      }}
                    >
                      +{plusIconCount - 1} {t('assets')}
                    </sup>
                  )}
                </Typography>
                <Typography sx={{ color: fontColor, fontSize: 12 }}>
                  {notification.makeModel}
                </Typography>
              </Box>
            </Box>

            <Typography sx={{ flex: 0.8, color: fontColor, fontSize: 14 }}>
              {convertUtcToDisplayTime(
                notification.notifiedDate,
                preferredTimeDisplayMode,
                timezone,
                DATE_FORMAT
              )}
            </Typography>

            <Typography
              sx={{
                flex: 1.5,
                color: fontColor,
                fontSize: 12,
              }}
              dangerouslySetInnerHTML={{
                __html: truncateText(notification.message, 100),
              }}
            />

            <Box
              sx={{
                flex: 0.5,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Chip
                label={notification.severity}
                sx={{
                  bgcolor: severityColor,
                  color: fontColor,
                  fontWeight: 600,
                  fontSize: 12,
                  height: 28,
                  borderRadius: 3,
                }}
              />
            </Box>
          </>
        )}

        <Box sx={{ width: 48, display: 'flex', justifyContent: 'center' }}>
          <IconButton
            size="small"
            sx={{
              color: '#94a3b8',
              transition: 'transform 0.3s',
              transform: itemOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <ExpandMore
              onClick={() => {
                handleClick();
              }}
            />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={itemOpen} timeout="auto" unmountOnExit>
        <Box
          sx={{
            padding: '20px',
            bgcolor: enableDarkTheme
              ? palette.menuOptions
              : palette.boxBackground,
            borderBottom: '1px solid #1e3a5f',
            borderRight: '1px solid #1e3a5f',
            borderLeft: '1px solid #1e3a5f',
            borderBottomRightRadius: '4px',
            borderBottomLeftRadius: '4px',
          }}
        >
          {displayType && (
            <ViewComponent
              enableDarkTheme={enableDarkTheme}
              notification={notification}
              submitFeedback={submitFeedback}
            />
          )}
          <Footer
            enableDarkTheme={enableDarkTheme}
            setItemOpen={setItemOpen}
            acknowledgeNotification={acknowledgeAlert}
            isAcknowledged={notification.acknowledged}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
